import Divider from 'flow/components/ui-components/Divider';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import RadioButton from 'flow/components/ui-components/RadioButton';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import React, { MouseEventHandler } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import Page from '../../Page';
import SettingRow from '../../SettingRow';
import SettingSection from '../../SettingSection';
import formatDuration from '../functions/formatDuration';
import onUpdate from '../functions/onUpdate';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { useParams } from 'react-router-dom';

interface Props {
    profile: TypedMap<Profile>;
    onClickBack: MouseEventHandler;
}

const AvailableLandline = ({ profile, onClickBack }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { profiles } = useProfiles(extensionKey);
    const initialDelays = [5, 10, 15, 20, 25, 30, 45, 60, 90, 120];

    const parallelCallType = profile.getIn([
        'parallelCalls',
        'parallelCallType',
    ]);

    const incomingCallOptions = profile.getIn([
        'parallelCalls',
        'incomingCallOptions',
    ]);

    const delayTypeOptions = profile.getIn([
        'parallelCalls',
        'delayTypeOptions',
    ]);

    const delayType = profile.getIn(['parallelCalls', 'delayType']);
    const mexDelay = profile.getIn(['parallelCalls', 'mexDelay']);

    return (
        <Page onClickBack={onClickBack} header="settings.incoming_calls.fixed">
            <SettingSection>{i18next.t('settings.title.logic')}</SettingSection>

            {incomingCallOptions.map((incomingCallOption) => (
                <RadioButton
                    key={`incomingCallOption-${incomingCallOption}`}
                    name="incomingCallOptions"
                    checked={incomingCallOption === parallelCallType}
                    onChange={() =>
                        dispatch(
                            onUpdate(extensionKey, profile.get('key'), [
                                {
                                    state: [
                                        'parallelCalls',
                                        'parallelCallType',
                                    ],
                                    value: incomingCallOption,
                                },
                            ]),
                        )
                    }
                >
                    {i18next.t(
                        `settings.incoming_calls.fixed.${incomingCallOption}`,
                    )}
                </RadioButton>
            ))}

            {parallelCallType === 'both' && (
                <>
                    <Divider />

                    <SettingSection>
                        {i18next.t('settings.incoming_calls.optionlist.delay')}
                    </SettingSection>

                    {delayTypeOptions.map((delayTypeOption) => {
                        const getDelayTitle = () => {
                            if (delayTypeOption === 'no_delay') {
                                return i18next.t(
                                    'settings.incoming_calls.delay.none',
                                );
                            }
                            if (delayTypeOption === 'mobile') {
                                return i18next.t(
                                    'settings.incoming_calls.optionlist.delay_mobile',
                                );
                            }
                            return i18next.t(
                                'settings.incoming_calls.optionlist.delay_landline',
                            );
                        };

                        return (
                            <RadioButton
                                key={`delayTypeOptions-${delayTypeOption}`}
                                name="delayTypeOptions"
                                checked={delayType === delayTypeOption}
                                onChange={() => {
                                    dispatch(
                                        onUpdate(
                                            extensionKey,
                                            profile.get('key'),
                                            [
                                                {
                                                    state: [
                                                        'parallelCalls',
                                                        'delayType',
                                                    ],
                                                    value: delayTypeOption,
                                                },
                                                {
                                                    state: [
                                                        'parallelCalls',
                                                        'mexDelay',
                                                    ],
                                                    value: 5,
                                                },
                                            ],
                                            profiles,
                                            profile,
                                        ),
                                    );
                                }}
                            >
                                {getDelayTitle()}
                            </RadioButton>
                        );
                    })}

                    {(delayType === 'mobile' || delayType === 'fixed') && (
                        <DropdownMenuComponent
                            title={`${i18next.t(
                                'settings.incoming_calls.optionlist.delay_time',
                            )} (${formatDuration(mexDelay)})`}
                            options={initialDelays.map((item) => ({
                                key: item.toString(),
                                label: formatDuration(item),
                            }))}
                            onItemSelected={(value) =>
                                dispatch(
                                    onUpdate(
                                        extensionKey,
                                        profile.get('key'),
                                        [
                                            {
                                                state: [
                                                    'parallelCalls',
                                                    'mexDelay',
                                                ],
                                                value: value,
                                            },
                                        ],
                                        profiles,
                                        profile,
                                    ),
                                )
                            }
                        />
                    )}
                </>
            )}

            {parallelCallType !== 'mobile' && (
                <>
                    <Divider />

                    <SettingSection>
                        {i18next.t('settings.advanced')}
                    </SettingSection>

                    <SettingRow
                        cta={
                            <Toggle
                                value={profile.get('callMVoip')}
                                onChange={() =>
                                    dispatch(
                                        onUpdate(
                                            extensionKey,
                                            profile.get('key'),
                                            [
                                                {
                                                    state: ['callMVoip'],
                                                    value: !profile.get(
                                                        'callMVoip',
                                                    ),
                                                },
                                            ],
                                        ),
                                    )
                                }
                                text={i18next.t(
                                    'settings.incoming_calls.mvoip',
                                )}
                            />
                        }
                    >
                        {i18next.t('settings.incoming_calls.mvoip')}
                    </SettingRow>

                    <p className="mt-2 ps-2 text-xs text-gray-500">
                        {i18next.t('settings.incoming_calls_mvoip.warning')}
                    </p>
                </>
            )}
        </Page>
    );
};

export default AvailableLandline;
